import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { ANDatePicker, ANTimePicker } from "@answersnow/pkg-theme";
import { ArrowRight, CaretDown, X } from "@phosphor-icons/react";
import OverrideChip from "./OverrideChip";

export interface IncidentTypeOption {
  value: string;
  label: string;
}

export interface SeverityLevelOption {
  value: string;
  label: string;
}

/** Yes/No tri-state — `null` means unset. */
export type YesNo = boolean | null;

/**
 * Shape of the incident-report form. Lives in `GenerateDocumentDialog` state so
 * it survives Back/Forward navigation. Each field's name matches the MIRA
 * `platformContext.incidentReport` payload key.
 */
export const OTHER_PARTY_VALUE = "Other";

export const SEVERITY_LEVEL_OPTIONS: SeverityLevelOption[] = [
  { value: "Near Miss", label: "Near Miss" },
  { value: "Minor", label: "Minor" },
  { value: "Moderate", label: "Moderate" },
  { value: "Serious", label: "Serious" },
  { value: "Critical", label: "Critical" },
];

export interface IncidentReportFormState {
  timeOfIncident: Date | null;
  locationOfIncident: string;
  partiesPresent: string[];
  otherPartiesPresent: string;
  isCrisisPlanWarranted: YesNo;
  wasCrisisPlanFollowed: YesNo;
  injuredOrEndangeredParty: string;
  locationOfInjury: string;
  wasMedicalAttentionNecessary: YesNo;
  wasClinicalTeamInformedWithin24Hours: YesNo;
  howCaregiverWasNotified: string;
  dateCaregiverWasNotified: Date | null;
  timeCaregiverWasNotified: Date | null;
  incidentType: string;
  severityLevel: string;
  incidentDetails: string;
  primaryResponder: string;
  actionsTaken: string;
  planForFutureEventsProactivePrevention: string;
  additionalInformation: string;
}

export const INITIAL_INCIDENT_REPORT_FORM: IncidentReportFormState = {
  timeOfIncident: null,
  locationOfIncident: "",
  partiesPresent: [],
  otherPartiesPresent: "",
  isCrisisPlanWarranted: null,
  wasCrisisPlanFollowed: null,
  injuredOrEndangeredParty: "",
  locationOfInjury: "",
  wasMedicalAttentionNecessary: null,
  wasClinicalTeamInformedWithin24Hours: null,
  howCaregiverWasNotified: "",
  dateCaregiverWasNotified: null,
  timeCaregiverWasNotified: null,
  incidentType: "",
  severityLevel: "",
  incidentDetails: "",
  primaryResponder: "",
  actionsTaken: "",
  planForFutureEventsProactivePrevention: "",
  additionalInformation: "",
};

export const isIncidentReportFormValid = (
  form: IncidentReportFormState,
  hasCrisisPlan?: boolean | null
): boolean => {
  if (!form.timeOfIncident) return false;
  if (!form.locationOfIncident.trim()) return false;
  const committedParties = form.partiesPresent.filter((p) => p !== OTHER_PARTY_VALUE);
  if (committedParties.length === 0) return false;
  if (form.partiesPresent.includes(OTHER_PARTY_VALUE)) return false;
  if (hasCrisisPlan === true && form.wasCrisisPlanFollowed === null) return false;
  if (hasCrisisPlan === false && form.isCrisisPlanWarranted === null) return false;
  if (!form.injuredOrEndangeredParty) return false;
  if (!form.locationOfInjury.trim()) return false;
  if (form.wasMedicalAttentionNecessary === null) return false;
  if (form.wasClinicalTeamInformedWithin24Hours === null) return false;
  if (!form.howCaregiverWasNotified.trim()) return false;
  if (!form.dateCaregiverWasNotified) return false;
  if (!form.timeCaregiverWasNotified) return false;
  if (!form.incidentType) return false;
  if (!form.severityLevel) return false;
  if (!form.incidentDetails.trim()) return false;
  if (!form.primaryResponder) return false;
  if (!form.actionsTaken.trim()) return false;
  if (!form.planForFutureEventsProactivePrevention.trim()) return false;
  return true;
};

/**
 * Resolves "Other" placeholders to the user-typed value and drops the auxiliary
 * `other*` fields from the payload. Match the session notes display behavior.
 */
export const collapseIncidentReportPayload = (form: IncidentReportFormState) => {
  const {
    otherPartiesPresent,
    partiesPresent,
    timeOfIncident,
    dateCaregiverWasNotified,
    timeCaregiverWasNotified,
    ...rest
  } = form;
  const resolve = (v: string, other: string) => (v === OTHER_PARTY_VALUE ? other.trim() || v : v);
  return {
    ...rest,
    timeOfIncident: timeOfIncident ? format(timeOfIncident, "h:mm a") : null,
    dateCaregiverWasNotified: dateCaregiverWasNotified
      ? format(dateCaregiverWasNotified, "MM/dd/yyyy")
      : null,
    timeCaregiverWasNotified: timeCaregiverWasNotified
      ? format(timeCaregiverWasNotified, "h:mm a")
      : null,
    partiesPresent: partiesPresent.map((p) => resolve(p, otherPartiesPresent)),
  };
};

interface IncidentReportStepProps {
  /** Whether an active override waives this document's readiness checks */
  isOverridden?: boolean;
  form: IncidentReportFormState;
  onFieldChange: <K extends keyof IncidentReportFormState>(
    field: K,
    value: IncidentReportFormState[K]
  ) => void;
  partyOptions: string[];
  incidentTypeOptions: IncidentTypeOption[];
  severityLevelOptions: SeverityLevelOption[];
  hasCrisisPlan?: boolean | null;
  onClose: () => void;
  onContinue: () => void;
}

const yesNoToString = (v: YesNo): string => (v === true ? "yes" : v === false ? "no" : "");
const stringToYesNo = (s: string): YesNo => (s === "yes" ? true : s === "no" ? false : null);

const yesNoLabelSx = {
  "& .MuiFormControlLabel-label": {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "22px",
    letterSpacing: "0.28px",
    color: "text.primary",
  },
};

const YesNoRadio: React.FC<{
  value: YesNo;
  onChange: (v: YesNo) => void;
  name: string;
}> = ({ value, onChange, name }) => (
  <RadioGroup
    row={false}
    name={name}
    value={yesNoToString(value)}
    onChange={(_, v) => onChange(stringToYesNo(v))}
  >
    <FormControlLabel value="yes" control={<Radio />} label="Yes" sx={yesNoLabelSx} />
    <FormControlLabel value="no" control={<Radio />} label="No" sx={yesNoLabelSx} />
  </RadioGroup>
);

const IncidentReportStep: React.FC<IncidentReportStepProps> = ({
  form,
  onFieldChange,
  partyOptions,
  incidentTypeOptions,
  severityLevelOptions,
  hasCrisisPlan,
  onClose,
  onContinue,
  isOverridden = false,
}) => {
  const partiesHasOther = form.partiesPresent.includes(OTHER_PARTY_VALUE);

  const commitPartiesOther = () => {
    const name = form.otherPartiesPresent.trim();
    const withoutSentinel = form.partiesPresent.filter((p) => p !== OTHER_PARTY_VALUE);
    if (!name || withoutSentinel.includes(name)) {
      onFieldChange("partiesPresent", withoutSentinel);
    } else {
      onFieldChange("partiesPresent", [...withoutSentinel, name]);
    }
    onFieldChange("otherPartiesPresent", "");
  };

  return (
    <>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 7,
          py: 5,
        }}
      >
        <Box>
          <Typography variant="h4" component="h2">
            Incident Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Record the details of a client incident, including any injuries, crisis plan actions
            taken, and steps to prevent future occurrences.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close dialog"
          size="small"
          sx={{ color: "text.secondary" }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ px: 7, pb: 7, pt: 0, "& .MuiFormLabel-asterisk": { color: "error.main" } }}
      >
        <Stack sx={{ mt: 7, gap: 5 }}>
          {isOverridden && <OverrideChip />}
          <ANTimePicker
            label="Time of incident"
            required
            value={form.timeOfIncident}
            onChange={(v: unknown) => onFieldChange("timeOfIncident", v as Date | null)}
            fullWidth
            inputProps={{ placeholder: "" }}
          />

          <TextField
            label="Location of incident"
            required
            fullWidth
            multiline
            minRows={3}
            value={form.locationOfIncident}
            onChange={(e) => onFieldChange("locationOfIncident", e.target.value)}
            helperText='This should be the location where the client was physically located when the incident occurred, not "telehealth".'
          />

          <Box>
            <Autocomplete
              multiple
              options={[...partyOptions, OTHER_PARTY_VALUE]}
              value={form.partiesPresent}
              onChange={(_, v) => onFieldChange("partiesPresent", v as string[])}
              popupIcon={<CaretDown />}
              sx={{ "& .MuiAutocomplete-popupIndicator": { color: "action.active" } }}
              renderTags={(value, getTagProps) =>
                value.map((name, index) => (
                  <Chip {...getTagProps({ index })} key={`${name}-${index}`} label={name} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parties present"
                  required
                  placeholder={form.partiesPresent.length === 0 ? "Select parties present..." : ""}
                />
              )}
            />
            {partiesHasOther && (
              <TextField
                fullWidth
                label="Other party present"
                helperText="Type a name and press Enter"
                value={form.otherPartiesPresent}
                onChange={(e) => onFieldChange("otherPartiesPresent", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitPartiesOther();
                  }
                }}
                sx={{ mt: 2 }}
              />
            )}
          </Box>

          {hasCrisisPlan === true && (
            <FormControl required>
              <FormLabel>Was the Crisis Plan followed?</FormLabel>
              <YesNoRadio
                name="wasCrisisPlanFollowed"
                value={form.wasCrisisPlanFollowed}
                onChange={(v) => onFieldChange("wasCrisisPlanFollowed", v)}
              />
            </FormControl>
          )}

          {hasCrisisPlan === false && (
            <FormControl required>
              <FormLabel>Is a Crisis Plan warranted for future events</FormLabel>
              <YesNoRadio
                name="isCrisisPlanWarranted"
                value={form.isCrisisPlanWarranted}
                onChange={(v) => onFieldChange("isCrisisPlanWarranted", v)}
              />
            </FormControl>
          )}

          <Box>
            <TextField
              label="Injured or endangered party"
              required
              select
              fullWidth
              value={form.injuredOrEndangeredParty}
              onChange={(e) => onFieldChange("injuredOrEndangeredParty", e.target.value)}
              helperText="Select from the parties added above. No options yet? Start by adding parties to the list."
              SelectProps={{
                IconComponent: CaretDown,
                displayEmpty: true,
                renderValue: (val) => {
                  const v = val as string;
                  if (!v) return <em>Select party injured or endangered...</em>;
                  return v;
                },
                sx: { "& .MuiSelect-icon": { color: "action.active" } },
              }}
            >
              <MenuItem value="" disabled>
                <em>Select party injured or endangered...</em>
              </MenuItem>
              {form.partiesPresent
                .filter((opt) => opt !== OTHER_PARTY_VALUE)
                .map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
            </TextField>
          </Box>

          <TextField
            label="Location of injury"
            required
            fullWidth
            multiline
            minRows={3}
            value={form.locationOfInjury}
            onChange={(e) => onFieldChange("locationOfInjury", e.target.value)}
            helperText="The body part that sustained injury."
          />

          <FormControl required>
            <FormLabel>Was medical attention necessary?</FormLabel>
            <YesNoRadio
              name="wasMedicalAttentionNecessary"
              value={form.wasMedicalAttentionNecessary}
              onChange={(v) => onFieldChange("wasMedicalAttentionNecessary", v)}
            />
          </FormControl>

          <FormControl required>
            <FormLabel>Was the clinical team informed within 24 hours?</FormLabel>
            <YesNoRadio
              name="wasClinicalTeamInformedWithin24Hours"
              value={form.wasClinicalTeamInformedWithin24Hours}
              onChange={(v) => onFieldChange("wasClinicalTeamInformedWithin24Hours", v)}
            />
          </FormControl>

          <TextField
            label="How was the caregiver notified?"
            required
            fullWidth
            multiline
            minRows={3}
            value={form.howCaregiverWasNotified}
            onChange={(e) => onFieldChange("howCaregiverWasNotified", e.target.value)}
          />

          <Stack direction="row" spacing={7}>
            <Box sx={{ flex: 1 }}>
              <ANDatePicker
                label="Date the caregiver was notified"
                required
                value={form.dateCaregiverWasNotified}
                onChange={(v: unknown) =>
                  onFieldChange("dateCaregiverWasNotified", v as Date | null)
                }
                slots={{ textField: TextField }}
                slotProps={{
                  textField: { fullWidth: true, required: true, inputProps: { placeholder: "" } },
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <ANTimePicker
                label="Time the caregiver was notified"
                required
                value={form.timeCaregiverWasNotified}
                onChange={(v: unknown) =>
                  onFieldChange("timeCaregiverWasNotified", v as Date | null)
                }
                fullWidth
                inputProps={{ placeholder: "" }}
              />
            </Box>
          </Stack>

          <TextField
            label="Incident type"
            required
            select
            fullWidth
            value={form.incidentType}
            onChange={(e) => onFieldChange("incidentType", e.target.value)}
            SelectProps={{
              IconComponent: CaretDown,
              displayEmpty: true,
              sx: { "& .MuiSelect-icon": { color: "action.active" } },
            }}
          >
            <MenuItem value="" disabled>
              <em>Select incident type...</em>
            </MenuItem>
            {incidentTypeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Severity level"
            required
            select
            fullWidth
            value={form.severityLevel}
            onChange={(e) => onFieldChange("severityLevel", e.target.value)}
            SelectProps={{
              IconComponent: CaretDown,
              displayEmpty: true,
              sx: { "& .MuiSelect-icon": { color: "action.active" } },
            }}
          >
            <MenuItem value="" disabled>
              <em>Select severity level...</em>
            </MenuItem>
            {severityLevelOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Incident details"
            required
            fullWidth
            multiline
            minRows={4}
            value={form.incidentDetails}
            onChange={(e) => onFieldChange("incidentDetails", e.target.value)}
            helperText="Describe objectively what occurred before, during, and after the incident. Include antecedents, observed behaviors, who was impacted, injuries/safety concerns, actions taken, and outcome."
          />

          <Box>
            <TextField
              label="Primary responder"
              required
              select
              fullWidth
              value={form.primaryResponder}
              onChange={(e) => onFieldChange("primaryResponder", e.target.value)}
              helperText="Indicate who in the client's physical environment responded to the incident."
              SelectProps={{
                IconComponent: CaretDown,
                displayEmpty: true,
                renderValue: (val) => {
                  const v = val as string;
                  if (!v) return <em>Identify the primary responder...</em>;
                  return v;
                },
                sx: { "& .MuiSelect-icon": { color: "action.active" } },
              }}
            >
              <MenuItem value="" disabled>
                <em>Identify the primary responder...</em>
              </MenuItem>
              {form.partiesPresent
                .filter((opt) => opt !== OTHER_PARTY_VALUE)
                .map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
            </TextField>
          </Box>

          <TextField
            label="Actions taken"
            required
            fullWidth
            multiline
            minRows={4}
            value={form.actionsTaken}
            onChange={(e) => onFieldChange("actionsTaken", e.target.value)}
            helperText="Please describe the immediate actions taken to maintain safety and respond to the incident, including any telehealth-related barriers that affected response (e.g., poor connection, caregiver unavailable, client moved off screen) and how those barriers were addressed."
          />

          <TextField
            label="Plan for future events/proactive prevention"
            required
            fullWidth
            multiline
            minRows={4}
            value={form.planForFutureEventsProactivePrevention}
            onChange={(e) =>
              onFieldChange("planForFutureEventsProactivePrevention", e.target.value)
            }
            helperText="Please describe how you plan to support and prevent future incidents from occurring (e.g., updating crisis plan with family, setting X, Y, and Z boundaries, adding a goal to focus on X)."
          />

          <TextField
            label="Additional information"
            fullWidth
            multiline
            minRows={4}
            value={form.additionalInformation}
            onChange={(e) => onFieldChange("additionalInformation", e.target.value)}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 7, pt: 5, pb: 5, gap: 2 }}>
        <Button variant="contained" size="medium" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          endIcon={<ArrowRight size={16} />}
          disabled={!isIncidentReportFormValid(form, hasCrisisPlan) && !isOverridden}
          onClick={onContinue}
        >
          Continue To Confirm
        </Button>
      </DialogActions>
    </>
  );
};

export default IncidentReportStep;
